import { useState } from 'react'

interface ArticleDetailProps {
  article: any
  isLoadingDetails?: boolean
}

export default function ArticleDetail({ article, isLoadingDetails }: ArticleDetailProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!article) return null

  return (
    <div className="bg-gray-50 border-l-4 border-blue-500 p-4">
      <div className="space-y-3">
        <div>
          <h4 className="font-semibold text-gray-900">{article.title || 'No title'}</h4>
        </div>

        {article.authors_full && (
          <div>
            <p className="text-sm text-gray-600">
              <strong>Authors:</strong> {article.authors_full}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-4 text-sm">
          {article.journal && (
            <span><strong>Journal:</strong> {article.journal}</span>
          )}
          {article.year && (
            <span><strong>Year:</strong> {article.year}</span>
          )}
          {article.quartile && (
            <span><strong>Quartile:</strong> {article.quartile}</span>
          )}
          {article.sjr_score && (
            <span><strong>SJR:</strong> {article.sjr_score}</span>
          )}
        </div>

        {/* Citation Metrics */}
        {(article.citations != null || article.rcr != null || article.field_citation_rate != null || article.nih_percentile != null) && (
          <div className="flex flex-wrap gap-3 text-sm bg-blue-50 p-2 rounded">
            <strong className="text-blue-900">iCite:</strong>
            {article.citations != null && (
              <span><strong>Citations:</strong> {article.citations}</span>
            )}
            {article.citations_per_year != null && (
              <span><strong>Cites/yr:</strong> {article.citations_per_year.toFixed(1)}</span>
            )}
            {article.rcr != null && (
              <span><strong>RCR:</strong> {article.rcr.toFixed(2)}</span>
            )}
            {article.field_citation_rate != null && (
              <span><strong>Field Rate:</strong> {article.field_citation_rate.toFixed(2)}</span>
            )}
            {article.nih_percentile != null && (
              <span><strong>NIH %ile:</strong> {(article.nih_percentile * 100).toFixed(1)}%</span>
            )}
          </div>
        )}

        {article.doi && (
          <div className="text-sm">
            <strong>DOI:</strong>{' '}
            <a 
              href={`https://doi.org/${article.doi}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {article.doi}
            </a>
          </div>
        )}

        {article.mesh_terms && article.mesh_terms.length > 0 && (
          <div className="text-sm">
            <strong>MeSH:</strong> {article.mesh_terms.join('; ')}
          </div>
        )}

        {article.keywords && article.keywords.length > 0 && (
          <div className="text-sm">
            <strong>Keywords:</strong> {article.keywords.join('; ')}
          </div>
        )}

        {article.affiliations && article.affiliations.length > 0 && (
          <div className="text-sm">
            <strong>Affiliations:</strong>
            <ul className="list-disc list-inside mt-1">
              {article.affiliations.map((affil: string, idx: number) => (
                <li key={idx} className="text-gray-600">{affil}</li>
              ))}
            </ul>
          </div>
        )}

        {article.author_emails && article.author_emails.length > 0 && (
          <div className="text-sm bg-blue-50 p-2 rounded">
            <strong>Contact:</strong>{' '}
            {article.author_emails.map((email: string, idx: number) => (
              <a 
                key={idx}
                href={`mailto:${email}`}
                className="text-blue-600 hover:underline mr-2"
              >
                {email}
              </a>
            ))}
          </div>
        )}

        {article.abstract ? (
          <div className="mt-3">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-blue-600 hover:underline mb-2"
            >
              {isExpanded ? 'Hide Abstract' : 'Show Abstract'}
            </button>

            {isExpanded && (
              <div className="bg-white p-3 rounded border text-sm max-h-64 overflow-y-auto">
                {article.abstract}
              </div>
            )}
          </div>
        ) : isLoadingDetails ? (
          <div className="mt-3 text-xs text-gray-500 italic">Loading abstract…</div>
        ) : null}
      </div>
    </div>
  )
}
